/**
 * 基本信息Tab字段描述配置
 * 定义基本信息tab中服务字段的显示配置
 */

import React from "react";
import { Tag } from "antd";
import type { DescriptionItem } from "@ms/shared-components/comps/CompsDescription";
import { getIconMap } from "@ms/shared-components";
import { SERVICE_STATUS_MAP } from "../service-apis/serviceTypes";

/**
 * 创建状态 Tag 的辅助函数
 */
const createStatusTag = (text: string, color: "success" | "error" | "warning" | "processing" | "default" = "default"): React.ReactNode => {
  return React.createElement(Tag, { color }, text);
};

/**
 * 创建是/否 Tag 的辅助函数
 */
const createYesNoTag = (value: boolean): React.ReactNode => {
  return value ? createStatusTag("是", "success") : createStatusTag("否", "default");
};

/**
 * 创建图标显示的辅助函数
 */
const createIconDisplay = (iconName: string | undefined): React.ReactNode => {
  if (!iconName) {
    return "-";
  }

  const iconMap = getIconMap();
  const IconComponent = iconMap[iconName];

  if (IconComponent) {
    // 只显示图标，不显示名称
    return React.createElement(IconComponent, {
      style: { fontSize: 18, color: "#1890ff" }
    });
  }

  // 如果找不到对应的图标组件，显示图标名称文本
  return iconName;
};

// 服务基本信息字段配置接口
export interface ServiceFieldConfig {
  // 字段名（支持驼峰和下划线两种命名）
  fieldName: string | string[];
  // 显示标签
  label: string;
  // 是否必显示（false表示仅在值存在时显示）
  required?: boolean;
  // 值转换函数（可以返回字符串或 React 节点）
  valueFormatter?: (value: any, serviceDetail: any) => string | React.ReactNode;
  // 跨列数
  span?: number;
}

// 服务基本信息字段配置列表（按显示顺序）
export const SERVICE_FIELD_CONFIGS: ServiceFieldConfig[] = [
  {
    fieldName: ["name", "service_name"],
    label: "服务名称",
  },
  {
    fieldName: ["displayName", "display_name"],
    label: "显示名称",
  },
  {
    fieldName: ["description", "service_description"],
    label: "描述",
    span: 2,
  },
  {
    fieldName: ["serviceUrl", "url", "service_url"],
    label: "前端地址",
    span: 2,
  },
  {
    fieldName: ["backendUrl", "serviceEndpoint", "service_endpoint"],
    label: "后端地址",
    span: 2,
  },
  {
    fieldName: ["healthCheckUrl", "health_check_url"],
    label: "健康检查地址",
    span: 2,
  },
  {
    fieldName: "service_secret",
    label: "服务秘钥",
    span: 2,
    required: true, // 即使值为空也显示
    valueFormatter: (value) => {
      if (!value || value === "") {
        return "-";
      }
      // 对秘钥进行脱敏处理，只显示前4位和后4位，中间用*代替
      if (value.length > 8) {
        const start = value.substring(0, 4);
        const end = value.substring(value.length - 4);
        return `${start}${"*".repeat(Math.min(value.length - 8, 8))}${end}`;
      }
      // 如果秘钥长度小于等于8，全部用*代替
      return "*".repeat(value.length);
    },
  },
  {
    fieldName: ["icon", "iconName", "icon_name"],
    label: "图标",
    valueFormatter: (value) => createIconDisplay(value),
  },
  {
    fieldName: "version",
    label: "版本",
  },
  {
    fieldName: ["serviceCategory", "service_category"],
    label: "服务分类",
  },
  {
    fieldName: ["serviceType", "service_type"],
    label: "服务类型",
    valueFormatter: (value) => {
      const typeMap: Record<number, string> = {
        0: "系统",
        1: "后台",
        2: "中台",
        3: "前台",
      };
      return typeMap[value as number] || String(value || 0);
    },
  },
  {
    fieldName: ["routePath", "route_path"],
    label: "路由路径",
  },
  {
    fieldName: ["status", "service_status"],
    label: "运行状态",
    valueFormatter: (value) => {
      const statusInfo = SERVICE_STATUS_MAP[value as keyof typeof SERVICE_STATUS_MAP];
      if (statusInfo) {
        // 直接使用状态映射中定义的颜色
        return createStatusTag(
          statusInfo.text,
          statusInfo.color as "success" | "error" | "warning" | "default"
        );
      }
      // 如果找不到对应的状态，显示默认标签
      return createStatusTag(String(value), "default");
    },
  },
  {
    fieldName: ["isSystemService", "is_system_service"],
    label: "系统服务",
    valueFormatter: (value) => createYesNoTag(value),
  },

  {
    fieldName: ["isActive", "is_active"],
    label: "是否激活",
    valueFormatter: (value) => createYesNoTag(value),
  },

  {
    fieldName: ["healthStatus", "health_status"],
    label: "健康状态",
    valueFormatter: (value) => {
      return value ? createStatusTag("健康", "success") : createStatusTag("异常", "error");
    },
  },
  {
    fieldName: ["lastHealthCheck", "last_health_check"],
    label: "最后健康检查时间",
    valueFormatter: (value) => (value ? new Date(value).toLocaleString("zh-CN") : ""),
  },

  {
    fieldName: ["isMenuItem", "is_menu_item"],
    label: "是否显示在菜单",
    valueFormatter: (value) => createYesNoTag(value),
  },
  {
    fieldName: ["menuOrder", "menu_order"],
    label: "菜单顺序",
    valueFormatter: (value) => String(value),
  },

  {
    fieldName: ["requiresAuth", "requires_auth"],
    label: "需要认证",
    valueFormatter: (value) => createYesNoTag(value),
  },
  {
    fieldName: ["requiredPermissions", "permissions", "required_permissions"],
    label: "所需权限",
    span: 2,
  },
  {
    fieldName: ["createdAt", "created_at"],
    label: "创建时间",
    valueFormatter: (value) => (value ? new Date(value).toLocaleString("zh-CN") : ""),
  },
  {
    fieldName: ["updatedAt", "updated_at"],
    label: "更新时间",
    valueFormatter: (value) => (value ? new Date(value).toLocaleString("zh-CN") : ""),
  },
];

/**
 * 从serviceDetail对象中获取字段值（支持驼峰和下划线两种命名）
 */
export const getServiceFieldValue = (serviceDetail: any, fieldName: string | string[]): any => {
  if (Array.isArray(fieldName)) {
    // 尝试多个字段名
    for (const name of fieldName) {
      if (serviceDetail[name] !== undefined && serviceDetail[name] !== null) {
        return serviceDetail[name];
      }
    }
    return undefined;
  } else {
    return serviceDetail[fieldName];
  }
};

/**
 * 根据配置构建服务基本信息字段的描述项
 */
export const buildServiceDescriptionItems = (serviceDetail: any): DescriptionItem[] => {
  if (!serviceDetail) return [];

  const items: DescriptionItem[] = [];

  // 调试：打印原始数据（仅在开发环境）
  if (process.env.NODE_ENV === "development") {
    console.log("[buildServiceDescriptionItems] 服务详情数据:", serviceDetail);
  }

  // 遍历字段配置
  SERVICE_FIELD_CONFIGS.forEach((config) => {
    const value = getServiceFieldValue(serviceDetail, config.fieldName);

    // 调试：打印字段值（仅在开发环境）
    if (process.env.NODE_ENV === "development" && value === undefined) {
      console.log(
        `[buildServiceDescriptionItems] 字段 ${Array.isArray(config.fieldName) ? config.fieldName.join("|") : config.fieldName} 未找到值`,
        {
          fieldName: config.fieldName,
          availableKeys: Object.keys(serviceDetail),
        }
      );
    }


    // 判断是否显示该字段
    // 对于布尔值或数字0，需要特殊处理

    const shouldShow =
      config.required ||
      (value !== undefined &&
        value !== null &&
        value !== "" &&
        !(typeof value === "boolean" && value === false && !config.required));

    // 对于布尔值字段，即使为false也应该显示
    const isBooleanField =
      config.fieldName === "isMenuItem" ||
      config.fieldName === "is_menu_item" ||
      config.fieldName === "requiresAuth" ||
      config.fieldName === "requires_auth" ||
      config.fieldName === "external" ||
      config.fieldName === "is_external" ||
      config.fieldName === "isActive" ||
      config.fieldName === "is_active" ||
      config.fieldName === "healthStatus" ||
      config.fieldName === "health_status" ||
      config.fieldName === "isSystemService" ||
      config.fieldName === "is_system_service" ||
      (Array.isArray(config.fieldName) &&
        (config.fieldName.includes("isMenuItem") ||
          config.fieldName.includes("is_menu_item") ||
          config.fieldName.includes("requiresAuth") ||
          config.fieldName.includes("requires_auth") ||
          config.fieldName.includes("external") ||
          config.fieldName.includes("is_external") ||
          config.fieldName.includes("isActive") ||
          config.fieldName.includes("is_active") ||
          config.fieldName.includes("healthStatus") ||
          config.fieldName.includes("health_status") ||
          config.fieldName.includes("isSystemService") ||
          config.fieldName.includes("is_system_service")));

    // 对于数字字段，0也应该显示
    const isNumberField =
      config.fieldName === "menuOrder" ||
      config.fieldName === "menu_order" ||
      (Array.isArray(config.fieldName) &&
        (config.fieldName.includes("menuOrder") || config.fieldName.includes("menu_order")));

    const finalShouldShow =
      shouldShow ||
      (isBooleanField && value !== undefined) ||
      (isNumberField && value !== undefined && value !== null);

    if (finalShouldShow) {
      // 格式化值
      let formattedValue = value;
      if (config.valueFormatter) {
        formattedValue = config.valueFormatter(value, serviceDetail);
      } else if (value !== undefined && value !== null) {
        formattedValue = String(value);
      }

      // 添加描述项
      items.push({
        key: Array.isArray(config.fieldName) ? config.fieldName[0] : config.fieldName,
        label: config.label,
        value: formattedValue,
        span: config.span,
      });
    }
  });

  return items;
};

